import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { isValidPhone } from '../helpers';
import { useUser } from '@src/state/user';
import { useEditing } from '@src/state/editing/hooks';
import { useFormState } from '@src/state/form/hooks';
import { updateAccountInfo } from '@src/state/form/actions';
import { FLOW_ID } from '../AppConstants';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useOnloading } from '@src/state/landing';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '5px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '20px',
  fontFamily: '$primary',
});

const AccountInfoLabel = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  marginBottom: '10px',
  fontFamily: '$primary',
});

const AccountInfoValue = styled('div', {
  fontSize: '16px',
  fontWeight: 700,
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

export const AccountInfo = ({
  phone,
  phoneExt,
  handleNext,
  handleBack,
  setPhoneNumber,
}) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const { currentFlow } = useOnloading.state();
  const currentUser = useUser.state();
  const { firstName, lastName, email } = currentUser;
  const [editPhone, setEditPhone] = useState(!currentUser.phone);
  const [editPhoneExt] = useState(!currentUser.phoneExt);
  const { isEditing } = useEditing.state();
  const { actions, state } = useFormState({ updateAccountInfo });
  const form = useForm();

  useEffect(() => {
    form.setValue(
      'user.phone',
      phone || currentUser?.phone || state?.registration?.accountInfo?.phone
    );
    form.setValue(
      'user.phoneExt',
      phoneExt ||
        currentUser?.phoneExt ||
        state?.registration?.accountInfo?.phoneExt
    );
  }, []);

  const onNext = () => {
    form.validate(
      'user.phone',
      (phn) => {
        setPhoneNumber({
          phone: phn,
        });
        form.validate('user.phoneExt', (ext) => {
          actions.updateAccountInfo({
            phoneExt: ext,
          });
          setPhoneNumber({
            phoneExt: ext,
          });
        });

        actions.updateAccountInfo({
          phone: phn,
        });

        handleNext();
      },
      () => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = ['Work Phone Number'];
          adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  return (
    <React.Fragment>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>Confirm your account information.</BoxHeaderText>
        </BoxHeader>
        <Description>
          Populated fields use your One Healthcare ID sign in credentials.
        </Description>
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              marginBottom: '25px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '48%',
              }}
            >
              <AccountInfoLabel>First Name*</AccountInfoLabel>
              <AccountInfoValue>{firstName}</AccountInfoValue>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '52%',
              }}
            >
              <AccountInfoLabel>Last Name*</AccountInfoLabel>
              <AccountInfoValue>{lastName}</AccountInfoValue>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '25px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '48%',
              }}
            >
              <AccountInfoLabel>Email Address*</AccountInfoLabel>
              <AccountInfoValue>{email}</AccountInfoValue>
            </div>
            <div
              style={{
                display: 'flex',
                width: '52%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '57%',
                }}
              >
                <FormProvider
                  state={form}
                  style={{ marginTop: '-5px', marginRight: '15px' }}
                >
                  <TextInput
                    id="user-phone"
                    model="user.phone"
                    label="Work Phone Number"
                    mask={currentUser?.phone?.length > 10 ? null : 'phone'}
                    aria-label="type here to enter phone number"
                    onClear={() => {
                      form.validate('user.phone');
                      setEditPhone(true);
                      actions.updateAccountInfo({
                        phone: '',
                      });
                    }}
                    onChange={(e) => {
                      setEditPhone(true);
                      form.validate('user.phone');
                      actions.updateAccountInfo({
                        phone: e.target.value,
                      });
                    }}
                    validators={{
                      required: 'A valid phone number is required',
                      validate: editPhone
                        ? {
                            isValidPhone: (value) => {
                              return (
                                isValidPhone(value) ||
                                'A valid phone number is required'
                              );
                            },
                          }
                        : undefined,
                    }}
                    isDisabled={!editPhone}
                    isClearable
                    css={{
                      'abyss-text-input-label': {
                        fontWeight: '600',
                        fontSize: '14px',
                      },
                    }}
                  />
                </FormProvider>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '30%',
                }}
              >
                <FormProvider state={form} style={{ marginTop: '-5px' }}>
                  <TextInput
                    id="user-phoneExt"
                    model="user.phoneExt"
                    label="Ext."
                    aria-label="type here to enter phone extension"
                    maxLength={10}
                    onClear={() => {
                      setPhoneNumber({
                        phone: state.registration?.accountInfo?.phone || '',
                        phoneExt:
                          state.registration?.accountInfo?.phoneExt || '',
                      });
                      actions.updateAccountInfo({
                        phone: '',
                        phoneExt: '',
                      });
                    }}
                    onChange={(e) => {
                      actions.updateAccountInfo({
                        phoneExt: e.target.value,
                      });
                    }}
                    onBlur={(e) => {
                      form.setValue('user.phoneExt', e.target.value);
                    }}
                    isDisabled={!editPhoneExt}
                    css={{
                      'abyss-text-input-label': {
                        fontWeight: '600',
                        fontSize: '14px',
                      },
                    }}
                  />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </MainContainer>
      <Flex
        css={{
          'abyss-flex-root': {
            justifyContent: 'space-between',
          },
        }}
      >
        <Flex.Content space-50>
          {!isEditing && (
            <BackButton
              onClick={() => {
                return handleBack();
              }}
            >
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content space-50>
          <NextButton onClick={onNext}>
            <NextButtonText>
              {currentFlow === FLOW_ID.PROVIDER || !isEditing ? 'Next' : 'Save'}
            </NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
    </React.Fragment>
  );
};

AccountInfo.propTypes = {
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  setPhoneNumber: PropTypes.func.isRequired,
  phone: PropTypes.string.isRequired,
  phoneExt: PropTypes.string.isRequired,
};
